import type { BanEntityType, BanRecord } from "../pages/administration/banData";

export interface BanStatusContext { userId: number; projectId: number; }
export interface BanStatusScope extends BanStatusContext { entity_type: BanEntityType; entity_value: string; }
export interface BanStatusSnapshot { rows: BanRecord[]; checkedAt: number; }
export const BAN_STATUS_TTL = 10 * 60_000;
export const banStatusKey = (scope: BanStatusScope) => JSON.stringify([scope.userId, scope.projectId, scope.entity_type, scope.entity_value]);
const contextKey = (scope: BanStatusContext) => JSON.stringify([scope.userId, scope.projectId]);
const entityKey = (type: unknown, value: unknown) => JSON.stringify([type, typeof value === "string" ? value.trim() : value]);
interface IndexedList { checkedAt: number; entities: Map<string, BanRecord[]>; }

/** Memory only. Shared requests belong to the cache, not to an individual popup. */
export const createBanStatusCache = (fetchRows: (scope: BanStatusContext, signal: AbortSignal) => Promise<BanRecord[]>, now = Date.now) => {
	const cache = new Map<string, { scope: BanStatusContext; snapshot: IndexedList }>();
	const pending = new Map<string, { scope: BanStatusContext; controller: AbortController; promise: Promise<IndexedList> }>();
	const listeners = new Set<{ scope: Pick<BanStatusScope, "projectId" | "userId">; callback: () => void }>();
	const matches = (a: Pick<BanStatusScope, "projectId" | "userId">, b: Pick<BanStatusScope, "projectId" | "userId">) => a.projectId === b.projectId && a.userId === b.userId;
	const peekList = (scope: BanStatusContext) => {
		const entry = cache.get(contextKey(scope));
		return entry && now() - entry.snapshot.checkedAt < BAN_STATUS_TTL ? entry.snapshot : undefined;
	};
	const select = (list: IndexedList, scope: BanStatusScope): BanStatusSnapshot => ({ checkedAt: list.checkedAt, rows: list.entities.get(entityKey(scope.entity_type, scope.entity_value)) ?? [] });
	const peek = (scope: BanStatusScope) => { const list = peekList(scope); return list ? select(list, scope) : undefined; };
	return {
		peek,
		async load(scope: BanStatusScope, refresh = false): Promise<BanStatusSnapshot> {
			const key = contextKey(scope);
			const ongoing = pending.get(key);
			if (ongoing) return select(await ongoing.promise, scope);
			const hit = !refresh && peek(scope);
			if (hit) return Promise.resolve(hit);
			cache.delete(key);
			const controller = new AbortController();
			let timer: ReturnType<typeof setTimeout>;
			const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(new Error("load")); }, 30_000); });
			const context = { userId: scope.userId, projectId: scope.projectId };
			const promise = Promise.race([Promise.resolve().then(() => fetchRows(context, controller.signal)), timeout]).then(rows => {
				if (controller.signal.aborted || pending.get(key)?.controller !== controller) throw new Error("contextChanged");
				const entities = new Map<string, BanRecord[]>();
				for (const row of rows) {
					const key = entityKey(row.entity_type, row.entity_value);
					const group = entities.get(key) ?? []; group.push(row); entities.set(key, group);
				}
				const snapshot = { entities, checkedAt: now() };
				cache.set(key, { scope: context, snapshot });
				while (cache.size > 8) cache.delete(cache.keys().next().value!);
				return snapshot;
			}).finally(() => { clearTimeout(timer); if (pending.get(key)?.controller === controller) pending.delete(key); });
			pending.set(key, { scope, controller, promise });
			return select(await promise, scope);
		},
		subscribe(scope: Pick<BanStatusScope, "projectId" | "userId">, callback: () => void) {
			const listener = { scope, callback }; listeners.add(listener);
			return () => { listeners.delete(listener); };
		},
		invalidate(scope: Pick<BanStatusScope, "projectId" | "userId">) {
			for (const [key, entry] of cache) if (matches(entry.scope, scope)) cache.delete(key);
			for (const [key, entry] of pending) if (matches(entry.scope, scope)) { entry.controller.abort(); pending.delete(key); }
			for (const listener of listeners) if (matches(listener.scope, scope)) listener.callback();
		},
		clear() {
			cache.clear();
			for (const entry of pending.values()) entry.controller.abort();
			pending.clear();
		},
	};
};
